// Hook reaproveitável para carregar um recurso da API (SEF-124).
//
// Padroniza o ciclo carregando/erro/dados em cima do cliente HTTP central
// (`apiFetch` de src/api.js, SEF-70) e das primitivas de estado
// EstadoCarregando / EstadoErro / EstadoVazio.
//
// Uso típico:
//   const { dados, carregando, erro, recarregar } = useRecurso(
//     () => apiFetch('/api/revistas'),
//     [],
//   );
//   if (carregando) return <EstadoCarregando />;
//   if (erro) return <EstadoErro mensagem={erro.code} onRetry={recarregar} />;
//   if (!dados?.length) return <EstadoVazio />;

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * @param {() => Promise<any>} buscador  função que retorna a promessa do recurso
 * @param {Array} deps  dependências que disparam recarga (como em useEffect)
 * @returns {{ dados: any, carregando: boolean, erro: Error|null, recarregar: () => Promise<void> }}
 */
export function useRecurso(buscador, deps = []) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const montadoRef = useRef(true);

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resultado = await buscador();
      if (montadoRef.current) setDados(resultado);
    } catch (e) {
      if (montadoRef.current) setErro(e);
    } finally {
      if (montadoRef.current) setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    montadoRef.current = true;
    buscar();
    return () => {
      montadoRef.current = false;
    };
  }, [buscar]);

  return { dados, carregando, erro, recarregar: buscar };
}
